import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserManagementEnabled } from '@/hooks/useUserManagementEnabled';
import {
  SpaceAssignment,
  SpaceType,
  SpaceRelation,
  GET_USER_SPACE_ASSIGNMENTS,
  GetUserSpaceAssignmentsResponse,
  GetUserSpaceAssignmentsVariables,
  AUTH_API_URL,
} from '@/pages/Settings/UserManagement/SpaceAssignments';
import {
  GET_USER,
  GetUserQueryResponse,
  GetUserQueryVariables,
} from '@/pages/Settings/UserManagement/Users';
import { useUserContext } from './UserContext';

// Space hierarchy levels from highest to lowest
// Hierarchy: site → building → floor → room/zone
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SPACE_HIERARCHY: SpaceType[] = ['site', 'building', 'floor', 'room', 'zone'];

// Query to fetch site hierarchy for parent lookups
const GET_SITE_HIERARCHY_FOR_ACCESS = gql`
  query GetSiteHierarchyForAccess {
    sites {
      data {
        id
        buildings {
          id
          floors {
            id
            rooms {
              id
            }
            zones {
              id
            }
          }
        }
      }
    }
  }
`;

// Types for hierarchy data
interface HierarchyRoom {
  id: string;
}

interface HierarchyZone {
  id: string;
}

interface HierarchyFloor {
  id: string;
  rooms?: HierarchyRoom[];
  zones?: HierarchyZone[];
}

interface HierarchyBuilding {
  id: string;
  floors?: HierarchyFloor[];
}

interface HierarchySite {
  id: string;
  buildings?: HierarchyBuilding[];
}

interface HierarchyResponse {
  sites?: {
    data?: HierarchySite[];
  };
}

// Parent chain for hierarchical access checking
interface ParentChain {
  floor?: string;
  building?: string;
  site?: string;
}

// Relations that grant write access (can modify spaces, sensors, etc.)
const WRITE_RELATIONS: SpaceRelation[] = ['admin', 'member', 'can_edit'];

interface SpaceAccessContextValue {
  // User's direct space assignments from OpenFGA
  assignments: SpaceAssignment[];
  // Loading state
  isLoading: boolean;
  // Error state
  error: Error | null;
  // Whether user has unrestricted access (admin/superuser, or feature flag off)
  isUnrestricted: boolean;
  // Whether the user has the Admin role in the Auth API (distinct from isUnrestricted)
  isAuthApiAdmin: boolean;
  // Whether the user has the Owner role in the Auth API
  isAuthApiOwner: boolean;
  // Check if user has access to a specific space (includes inherited access)
  hasAccess: (spaceType: SpaceType, spaceId: string, relation?: SpaceRelation) => boolean;
  // Check if user has write access to a specific space (admin, member, or can_edit)
  hasWriteAccess: (spaceType: SpaceType, spaceId: string) => boolean;
  // Check if user has any access (is not restricted)
  hasAnyAccess: () => boolean;
  // Get all accessible space IDs for a given type
  getAccessibleSpaceIds: (spaceType: SpaceType) => string[];
  // Refetch assignments
  refetch: () => void;
}

const SpaceAccessContext = createContext<SpaceAccessContextValue>({
  assignments: [],
  isLoading: true,
  error: null,
  isUnrestricted: false,
  isAuthApiAdmin: false,
  isAuthApiOwner: false,
  hasAccess: () => false, // Default to no access for safety
  hasWriteAccess: () => false, // Default to no write access for safety
  hasAnyAccess: () => false,
  getAccessibleSpaceIds: () => [],
  refetch: () => {},
});

// Convert Auth0 user ID (auth0|xxx) to backend format (user_xxx)
export const convertAuth0IdToUserId = (auth0Id: string): string => {
  if (auth0Id.startsWith('auth0|')) {
    const idPart = auth0Id.substring(6); // Remove 'auth0|' prefix
    // If the ID part already starts with 'user_', don't add another prefix
    if (idPart.startsWith('user_')) {
      return idPart;
    }
    return 'user_' + idPart;
  }
  return auth0Id;
};

export const SpaceAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const { isSuperUser, clientId } = useUserContext();
  const [userId, setUserId] = useState<string | null>(null);

  // Only apply space access gating when the user_management feature is enabled.
  // When disabled, all users get unrestricted access (no space-level filtering).
  const isUserManagementEnabled = useUserManagementEnabled();

  // UserContext is still loading if clientId is empty (useUser.tsx hasn't set it yet)
  // Using clientId instead of roles.length because clientId is always set after JWT decode
  const isUserContextLoading = isAuthenticated && !clientId;

  // Extract and convert user ID when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.sub) {
      const convertedId = convertAuth0IdToUserId(user.sub);
      setUserId(convertedId);
    }
  }, [isAuthenticated, user?.sub, user?.email]);

  // Fetch current user's Auth API profile to check for Admin role.
  // Uses errorPolicy 'all' so that users not found in the database (e.g. legacy users
  // without a row in the users table) are handled gracefully — data will be null and
  // isAuthApiAdmin will default to false.
  const { data: currentUserData, loading: isUserQueryLoading } = useQuery<
    GetUserQueryResponse,
    GetUserQueryVariables
  >(GET_USER, {
    variables: { id: userId || '' },
    skip: !userId || !isUserManagementEnabled || isSuperUser,
    context: { uri: AUTH_API_URL },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // Check if the current user has the Auth API Owner role
  const isAuthApiOwner = useMemo(() => {
    const authApiRoles = currentUserData?.user?.roles;
    if (!authApiRoles) return false;
    return authApiRoles.some((r) => r.toLowerCase() === 'owner');
  }, [currentUserData]);

  // Check if the current user has the Auth API Admin role (owners also pass admin-level gates)
  const isAuthApiAdmin = useMemo(() => {
    const authApiRoles = currentUserData?.user?.roles;
    if (!authApiRoles) return false;
    return authApiRoles.some((r) => r.toLowerCase() === 'admin' || r.toLowerCase() === 'owner');
  }, [currentUserData]);

  // Check if user has admin or superuser role
  // isSuperUser is set by useUser.tsx from the JWT access token claim 'http://butlr.io/roles'
  // isAuthApiAdmin is set from the Auth API user profile (e.g., roles: ["Admin"])
  // When user_management feature flag is disabled, all users are unrestricted (no space gating)
  const isUnrestricted = useMemo(() => {
    // If UserContext hasn't loaded yet, don't make access decisions
    if (isUserContextLoading) {
      return false;
    }
    // When user_management feature flag is off, bypass space access gating entirely
    if (!isUserManagementEnabled) {
      return true;
    }
    return isSuperUser || isAuthApiAdmin;
  }, [isSuperUser, isAuthApiAdmin, isUserContextLoading, isUserManagementEnabled]);

  // Fetch user's space assignments from auth API
  // Skip when user_management feature flag is disabled (no gating needed)
  const {
    data,
    loading: isQueryLoading,
    error: queryError,
    refetch,
  } = useQuery<GetUserSpaceAssignmentsResponse, GetUserSpaceAssignmentsVariables>(
    GET_USER_SPACE_ASSIGNMENTS,
    {
      variables: { user_id: userId || '' },
      skip: !userId || !isUserManagementEnabled,
      context: { uri: AUTH_API_URL },
      fetchPolicy: 'cache-and-network',
    },
  );

  // Fetch site hierarchy for parent lookups (hierarchical access checking)
  const { data: hierarchyData, loading: isHierarchyLoading } = useQuery<HierarchyResponse>(
    GET_SITE_HIERARCHY_FOR_ACCESS,
    {
      skip: !isAuthenticated || isUnrestricted, // Skip if not authenticated or already unrestricted
      fetchPolicy: 'cache-first',
    },
  );

  // Build parent lookup maps for hierarchical access checking
  // Maps: spaceId -> ParentChain (parent floor, building, site)
  const parentLookup = useMemo(() => {
    const lookup = new Map<string, ParentChain>();
    const sites = hierarchyData?.sites?.data || [];

    sites.forEach((site) => {
      // Sites have no parents
      lookup.set(site.id, {});

      site.buildings?.forEach((building) => {
        // Buildings have site as parent
        lookup.set(building.id, { site: site.id });

        building.floors?.forEach((floor) => {
          // Floors have building and site as parents
          lookup.set(floor.id, { building: building.id, site: site.id });

          floor.rooms?.forEach((room) => {
            // Rooms have floor, building, and site as parents
            lookup.set(room.id, { floor: floor.id, building: building.id, site: site.id });
          });

          floor.zones?.forEach((zone) => {
            // Zones have floor, building, and site as parents
            lookup.set(zone.id, { floor: floor.id, building: building.id, site: site.id });
          });
        });
      });
    });

    return lookup;
  }, [hierarchyData]);

  const assignments = useMemo(() => {
    return data?.userSpaceAssignments || [];
  }, [data]);

  // Build a lookup map for quick access checks
  // Key format: "spaceType:spaceId" -> Set of relations
  const accessMap = useMemo(() => {
    const map = new Map<string, Set<SpaceRelation>>();
    assignments.forEach((assignment) => {
      const key = `${assignment.space_type}:${assignment.space_id}`;
      if (!map.has(key)) {
        map.set(key, new Set());
      }
      map.get(key)!.add(assignment.relation);
    });
    return map;
  }, [assignments]);

  // Check if user has direct access to a space
  const hasDirectAccess = useCallback(
    (spaceType: SpaceType, spaceId: string, relation?: SpaceRelation): boolean => {
      const key = `${spaceType}:${spaceId}`;
      const relations = accessMap.get(key);
      if (!relations) return false;
      if (!relation) return true; // Any relation counts
      return relations.has(relation);
    },
    [accessMap],
  );

  // Check if user has access through parent hierarchy
  // If user has access to a parent space, they have access to all children
  const hasHierarchicalAccess = useCallback(
    (spaceType: SpaceType, spaceId: string, relation?: SpaceRelation): boolean => {
      // Get parent chain for this space
      const parents = parentLookup.get(spaceId);
      if (!parents) {
        return false;
      }

      // Check access to each parent in the hierarchy
      // Hierarchy: site → building → floor → room/zone
      if (parents.site && hasDirectAccess('site', parents.site, relation)) {
        return true;
      }
      if (parents.building && hasDirectAccess('building', parents.building, relation)) {
        return true;
      }
      if (parents.floor && hasDirectAccess('floor', parents.floor, relation)) {
        return true;
      }

      return false;
    },
    [parentLookup, hasDirectAccess],
  );

  // Check if user has access (direct or inherited through parent hierarchy)
  // Hierarchy: site → building → floor → room/zone
  // If user has access to a parent, they have access to all children
  const hasAccess = useCallback(
    (spaceType: SpaceType, spaceId: string, relation?: SpaceRelation): boolean => {
      // If user is admin or superuser, they have full access
      if (isUnrestricted) {
        return true;
      }
      // If user has no assignments, they have no access
      if (assignments.length === 0) {
        return false;
      }
      // Check direct access first
      if (hasDirectAccess(spaceType, spaceId, relation)) {
        return true;
      }
      // Check hierarchical access through parents
      return hasHierarchicalAccess(spaceType, spaceId, relation);
    },
    [isUnrestricted, assignments, hasDirectAccess, hasHierarchicalAccess],
  );

  // Check if user has write access (admin, member, or can_edit)
  // Write access allows modifying spaces, sensors, configurations, etc.
  const hasWriteAccess = useCallback(
    (spaceType: SpaceType, spaceId: string): boolean => {
      // If user is admin or superuser, they have full write access
      if (isUnrestricted) {
        return true;
      }
      // If user has no assignments, they have no access
      if (assignments.length === 0) {
        return false;
      }
      // Check if user has any write relation (direct or hierarchical)
      return WRITE_RELATIONS.some((relation) => hasAccess(spaceType, spaceId, relation));
    },
    [isUnrestricted, assignments, hasAccess],
  );

  // Check if user has any access at all
  const hasAnyAccess = useCallback((): boolean => {
    // If user is admin or superuser, they have full access
    if (isUnrestricted) {
      return true;
    }
    // If user has any assignments, they have some access
    if (assignments.length > 0) {
      return true;
    }
    // If user has NO assignments and is NOT admin/superuser, they have NO access
    return false;
  }, [isUnrestricted, assignments]);

  // Get all accessible space IDs for a given type
  const getAccessibleSpaceIds = useCallback(
    (spaceType: SpaceType): string[] => {
      return assignments.filter((a) => a.space_type === spaceType).map((a) => a.space_id);
    },
    [assignments],
  );

  const handleRefetch = useCallback(() => {
    if (userId) {
      refetch();
    }
  }, [userId, refetch]);

  const isLoading =
    isAuthLoading ||
    isQueryLoading ||
    isUserContextLoading ||
    isUserQueryLoading ||
    (!isUnrestricted && isHierarchyLoading);

  const error = useMemo(
    () => (queryError ? new Error(queryError.message) : null),
    [queryError?.message],
  );

  const value: SpaceAccessContextValue = useMemo(
    () => ({
      assignments,
      isLoading,
      error,
      isUnrestricted,
      isAuthApiAdmin,
      isAuthApiOwner,
      hasAccess,
      hasWriteAccess,
      hasAnyAccess,
      getAccessibleSpaceIds,
      refetch: handleRefetch,
    }),
    [
      assignments,
      isLoading,
      error,
      isUnrestricted,
      isAuthApiAdmin,
      isAuthApiOwner,
      hasAccess,
      hasWriteAccess,
      hasAnyAccess,
      getAccessibleSpaceIds,
      handleRefetch,
    ],
  );

  return <SpaceAccessContext.Provider value={value}>{children}</SpaceAccessContext.Provider>;
};

export const useSpaceAccess = () => {
  const context = useContext(SpaceAccessContext);
  return context;
};

export default SpaceAccessContext;
